import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, getToken } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { useAuth } from "@/lib/auth";
import {
  Loader2, AlertCircle, Sparkles, Cloud, HardDrive, Scissors,
  LogIn, LogOut, RotateCcw, Save, Trash2, VolumeX, Wand2,
  ZoomIn, ZoomOut, Maximize2, Repeat, Pause,
} from "lucide-react";

// Load Shaka lazily so we only pay the cost when a CDN preview actually opens
let _shakaPromise = null;
function loadShaka() {
  if (!_shakaPromise) {
    _shakaPromise = import("shaka-player/dist/shaka-player.compiled").then((mod) => {
      const shaka = mod.default || mod;
      try { shaka.polyfill?.installAll?.(); } catch { /* noop */ }
      return shaka;
    });
  }
  return _shakaPromise;
}

function fmtDur(sec) {
  const s = Math.max(0, Number(sec) || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = Math.floor(s % 60);
  return h > 0 ? `${h}h ${m}m ${r}s` : `${m}m ${r}s`;
}

// Parse & format seconds <-> "HH:MM:SS.ms" (ms optional, 3 digits)
function secToStr(sec) {
  const s = Math.max(0, Number(sec) || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s - h * 3600 - m * 60;
  const whole = Math.floor(r);
  const ms = Math.round((r - whole) * 1000);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(whole).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
}
function strToSec(str) {
  if (str == null) return null;
  const s = String(str).trim();
  if (!s) return null;
  // Accept HH:MM:SS(.ms) or MM:SS(.ms) or SS(.ms)
  const parts = s.split(":");
  let h = 0, m = 0, r = 0;
  if (parts.length === 3)      { h = Number(parts[0]); m = Number(parts[1]); r = Number(parts[2]); }
  else if (parts.length === 2) { m = Number(parts[0]); r = Number(parts[1]); }
  else                          { r = Number(parts[0]); }
  if ([h, m, r].some((n) => !Number.isFinite(n))) return NaN;
  return h * 3600 + m * 60 + r;
}

const CAN_EDIT_ROLES = new Set(["super_admin", "tenant_admin", "scheduler"]);

export default function AssetPreviewPlayer({ asset, open, onClose, onUpdated, overrideMode = false, overrideIn = null, overrideOut = null, onSaveOverride = null }) {
  const { user } = useAuth() || {};
  const canEdit = CAN_EDIT_ROLES.has(user?.role);

  const videoRef = useRef(null);
  const [videoEl, setVideoEl] = useState(null);
  const attachVideo = (el) => { videoRef.current = el; setVideoEl(el); };
  const playerRef = useRef(null);
  const [state, setState] = useState({ loading: true, error: null });
  const [currentTime, setCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);

  // Trim state, initialised from the asset. `null` == unset (that endpoint).
  const [tIn, setTIn] = useState(asset?.trim_in_sec ?? null);
  const [tOut, setTOut] = useState(asset?.trim_out_sec ?? null);
  const [tInStr, setTInStr] = useState("");
  const [tOutStr, setTOutStr] = useState("");
  const [saving, setSaving] = useState(false);
  // Req 1/2: distinguish an explicit "restore full length" (Reset to full /
  // Clear trim) from an ordinary save, and gate a "plays to the end" confirm.
  const [resetIntent, setResetIntent] = useState(false);
  const [confirmEndPlay, setConfirmEndPlay] = useState(false);
  const [silenceBusy, setSilenceBusy] = useState(false);
  const [waveformPeaks, setWaveformPeaks] = useState(asset?.waveform_peaks || []);
  const [waveformBusy, setWaveformBusy] = useState(false);
  // Silence-detect tunables (live-adjustable)
  const [silenceThresholdDb, setSilenceThresholdDb] = useState(-30);
  const [silenceMinDur, setSilenceMinDur] = useState(0.4);
  // Waveform zoom state (0..1 normalised)
  const [zoom, setZoom] = useState({ start: 0, end: 1 });
  const scrubberRef = useRef(null);
  const dragRef = useRef(null); // {kind:'pan'|'in'|'out', ...}
  // Zoom-adaptive high-res peaks for the visible window (sharp when zoomed in)
  const [windowPeaks, setWindowPeaks] = useState(null); // {start,end,peaks} | null
  const [winBusy, setWinBusy] = useState(false);
  const winReqRef = useRef(0);
  const winTimerRef = useRef(null);
  // Snap IN/OUT to nearest detected silence boundary
  const [snapSilence, setSnapSilence] = useState(false);
  const [sceneCuts, setSceneCuts] = useState(asset?.scene_cuts || []);
  const [sceneBusy, setSceneBusy] = useState(false);
  const [sceneThreshold, setSceneThreshold] = useState(0.4);
  const [snapTol, setSnapTol] = useState(0.75);
  const [dragGhost, setDragGhost] = useState(null);
  const fps = Number(asset?.fps) > 0 ? Number(asset.fps) : 25;
  const frameSec = 1 / fps;

  const hlsUrl = asset?.cloudflare_hls_url;
  const usingCdn = !!hlsUrl;
  // Trust the decoded media's real duration over stored metadata: a stale
  // DB duration (e.g. 4:27 for a 2:51 file) skews the timeline and seek math.
  const rawDuration = videoDuration || Number(asset?.source_duration_sec ?? asset?.duration_sec) || 0;

  // Reset local state whenever a different asset is opened
  useEffect(() => {
    if (!open || !asset) return;
    const startIn = overrideMode ? (overrideIn ?? asset.trim_in_sec ?? null) : (asset.cut_in_sec ?? asset.trim_in_sec ?? null);
    const startOut = overrideMode ? (overrideOut ?? asset.trim_out_sec ?? null) : (asset.cut_out_sec ?? asset.trim_out_sec ?? null);
    setTIn(startIn);
    setTOut(startOut);
    setTInStr(startIn != null ? secToStr(startIn) : "");
    setTOutStr(startOut != null ? secToStr(startOut) : "");
    setResetIntent(false);
    setConfirmEndPlay(false);
    setWaveformPeaks(asset.waveform_peaks || []);
    setSceneCuts(asset.scene_cuts || []);
    setState({ loading: true, error: null });
  }, [open, asset, overrideMode, overrideIn, overrideOut]);

  useEffect(() => {
    if (!open || !asset) return;
    const v = videoRef.current;
    if (!v) return;

    // canplay fires again after every seek / rebuffer. Only do the initial
    // seek-to-IN once per load, otherwise playback is yanked back to IN
    // (looks like a black frame stuck near the start).
    let initialSeekDone = false;
    const onCanPlay = () => {
      setState({ loading: false, error: null });
      setVideoDuration(v.duration || 0);
      if (initialSeekDone) return;
      initialSeekDone = true;
      // If asset opens with an existing IN point, seek there so the user sees the trimmed clip
      const seekIn = asset.cut_in_sec ?? asset.trim_in_sec;
      if (seekIn != null) {
        const maxT = Number.isFinite(v.duration) && v.duration > 0 ? Math.max(0, v.duration - 0.1) : Infinity;
        v.currentTime = Math.min(maxT, Math.max(0, seekIn));
      }
    };
    const onTime = () => setCurrentTime(v.currentTime || 0);
    const onError = () => {
      const code = v.error?.code;
      const msg = code === 4 ? "Format not supported by this browser" :
                  code === 3 ? "Decode error — file may be corrupt" :
                  code === 2 ? "Network error while streaming" :
                  "Playback failed";
      setState({ loading: false, error: msg });
    };
    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("error", onError);
    v.muted = true;

    const teardownShaka = async () => {
      if (playerRef.current) {
        try { await playerRef.current.destroy(); } catch { /* noop */ }
        playerRef.current = null;
      }
    };

    // Always preview from the origin MP4 (with the ?auth= token) — even for
    // Cloudflare Stream assets. Shaka/HLS fails to load in this app and
    // progressive MP4 gives the frame-accurate seeking that trimming needs.
    // Set src synchronously (no async wrapper) so it always lands on the
    // currently-mounted <video>, race-free under React StrictMode.
    if (!asset.source_url) {
      setState({ loading: false, error: "No source file available to preview" });
    } else {
      const token = getToken() || "";
      v.src = `${asset.source_url}${asset.source_url?.includes("?") ? "&" : "?"}auth=${encodeURIComponent(token)}`;
      v.play().catch(() => { /* autoplay policy */ });
    }

    return () => {
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("error", onError);
      try { v.pause(); v.removeAttribute("src"); v.load(); } catch { /* noop */ }
      teardownShaka();
    };
  }, [open, asset, usingCdn, hlsUrl, videoEl]);

  const [loopPreview, setLoopPreview] = useState(false);
  const [loopFlash, setLoopFlash] = useState(false);

  // Auto-pause at trim_out during preview; or loop IN→OUT when loop is enabled.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const inS = tIn ?? 0;
    const outS = tOut ?? rawDuration;
    if (loopPreview) {
      if (currentTime >= outS - 0.05 || currentTime < inS - 0.15) {
        try {
          v.currentTime = inS;
          if (v.paused) v.play().catch(() => {});
          setLoopFlash(true);
          setTimeout(() => setLoopFlash(false), 140); // brief black frame at the loop seam
        } catch { /* noop */ }
      }
      return;
    }
    if (tOut == null) return;
    if (currentTime >= tOut - 0.05) {
      try { v.pause(); } catch { /* noop */ }
    }
  }, [currentTime, tOut, tIn, loopPreview, rawDuration]);

  const toggleLoopPreview = () => {
    setLoopPreview((on) => {
      const next = !on;
      const v = videoRef.current;
      if (next && v) {
        try { v.currentTime = tIn ?? 0; v.play().catch(() => {}); } catch { /* noop */ }
      }
      return next;
    });
  };

  const effectiveDuration = useMemo(() => {
    if (!rawDuration) return 0;
    const inS = tIn ?? 0;
    const outS = tOut ?? rawDuration;
    return Math.max(0, outS - inS);
  }, [rawDuration, tIn, tOut]);

  const dirty = (tIn ?? null) !== (asset?.cut_in_sec ?? asset?.trim_in_sec ?? null) || (tOut ?? null) !== (asset?.cut_out_sec ?? asset?.trim_out_sec ?? null);

  const setInHere = () => {
    const v = videoRef.current;
    if (!v) return;
    let t = Math.max(0, Math.min(v.currentTime, rawDuration - 0.1));
    t = snapToSilence(t);
    setTIn(t); setTInStr(secToStr(t)); setResetIntent(false);
  };
  const setOutHere = () => {
    const v = videoRef.current;
    if (!v) return;
    let t = Math.max(0.1, Math.min(v.currentTime, rawDuration));
    t = snapToSilence(t);
    setTOut(t); setTOutStr(secToStr(t)); setResetIntent(false);
  };
  const jumpToIn = () => { const v = videoRef.current; if (v && tIn != null) v.currentTime = tIn; };
  const jumpToOut = () => { const v = videoRef.current; if (v && tOut != null) v.currentTime = tOut; };

  const applyInStr = () => {
    if (!tInStr.trim()) { setTIn(null); return; }
    const s = strToSec(tInStr);
    if (!Number.isFinite(s)) { toast.error("Invalid IN time — use HH:MM:SS.ms"); return; }
    if (s < 0 || s >= rawDuration) { toast.error(`IN must be between 00:00:00 and ${secToStr(rawDuration)}`); return; }
    setTIn(s); setTInStr(secToStr(s)); setResetIntent(false);
  };
  const applyOutStr = () => {
    if (!tOutStr.trim()) { setTOut(null); return; }
    const s = strToSec(tOutStr);
    if (!Number.isFinite(s)) { toast.error("Invalid OUT time — use HH:MM:SS.ms"); return; }
    if (s <= 0 || s > rawDuration) { toast.error(`OUT must be between 00:00:00 and ${secToStr(rawDuration)}`); return; }
    setTOut(s); setTOutStr(secToStr(s)); setResetIntent(false);
  };

  // Actual network save. resetFull=true restores the whole song (the ONLY path
  // that discards an existing OUT point).
  const doSave = async (resetFull) => {
    setSaving(true);
    try {
      if (overrideMode && onSaveOverride) {
        await onSaveOverride(resetFull ? null : tIn, resetFull ? null : tOut);
      } else {
        const reqBody = resetFull
          ? { reset_full: true }
          : { trim_in_sec: tIn, trim_out_sec: tOut };
        const r = await api.patch(`/assets/${asset.id}/trim`, reqBody);
        toast.success(resetFull
          ? "Restored to full length"
          : `Trim applied · broadcast length ${fmtDur(r.data.effective_duration_sec ?? r.data.duration_sec ?? rawDuration)}`);
        onUpdated?.(r.data);
      }
      setResetIntent(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save trim");
    }
    setSaving(false);
  };

  const save = () => {
    if (!resetIntent && tIn != null && tOut != null && tOut <= tIn) {
      toast.error("OUT must be greater than IN");
      return;
    }
    // Req 2: warn before a previously-trimmed song is about to play to the end.
    const existingOut = asset?.cut_out_sec ?? asset?.trim_out_sec ?? null;
    const wasTrimmedBefore = existingOut != null;
    const willPlayToEnd = resetIntent || (tOut != null && tOut >= (rawDuration - 0.5));
    if (wasTrimmedBefore && willPlayToEnd) {
      setConfirmEndPlay(true);
      return;
    }
    doSave(resetIntent);
  };

  const clearTrim = () => { setTIn(null); setTOut(null); setTInStr(""); setTOutStr(""); setResetIntent(true); };
  const resetToFull = () => {
    // IN → 0, OUT → full clip length, in one click. Play the whole track.
    const full = rawDuration || videoDuration || 0;
    setTIn(0); setTInStr(secToStr(0));
    if (full > 0) { setTOut(full); setTOutStr(secToStr(full)); }
    else { setTOut(null); setTOutStr(""); }
    setResetIntent(true);
  };
  const revert = () => {
    const rIn = overrideMode ? (overrideIn ?? asset?.trim_in_sec ?? null) : (asset?.cut_in_sec ?? asset?.trim_in_sec ?? null);
    const rOut = overrideMode ? (overrideOut ?? asset?.trim_out_sec ?? null) : (asset?.cut_out_sec ?? asset?.trim_out_sec ?? null);
    setTIn(rIn);
    setTOut(rOut);
    setResetIntent(false);
  };

  const fmtWhen = (iso) => {
    try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso || ""; }
  };
  const trimLabel = (i, o) => `${i != null ? secToStr(i) : "0:00"}–${o != null ? secToStr(o) : "end"}`;

  const detectSilence = async () => {
    setSilenceBusy(true);
    try {
      const r = await api.post(`/assets/${asset.id}/detect-silence`, {
        threshold_db: silenceThresholdDb,
        min_dur: silenceMinDur,
      });
      const inSec = r.data.trim_in_sec;
      const outSec = r.data.trim_out_sec;
      if (inSec == null && outSec == null) {
        toast(`No leading / trailing silence at ${silenceThresholdDb}dB · ${r.data.silence_ranges.length} internal range(s)`);
      } else {
        if (inSec != null) { setTIn(inSec); setTInStr(secToStr(inSec)); }
        if (outSec != null) { setTOut(outSec); setTOutStr(secToStr(outSec)); }
        setResetIntent(false);
        toast.success(
          `Silence @ ${silenceThresholdDb}dB — ${inSec != null ? "IN " + secToStr(inSec) : ""}${inSec != null && outSec != null ? " · " : ""}${outSec != null ? "OUT " + secToStr(outSec) : ""}. Review & Save.`
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silence detect failed");
    }
    setSilenceBusy(false);
  };

  const regenerateWaveform = async () => {
    setWaveformBusy(true);
    try {
      const r = await api.post(`/assets/${asset.id}/waveform?buckets=1200`);
      setWaveformPeaks(r.data.waveform_peaks || []);
      onUpdated?.({ ...asset, waveform_peaks: r.data.waveform_peaks });
      toast.success("Waveform generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Waveform generation failed");
    }
    setWaveformBusy(false);
  };

  // Peaks sliced to the visible zoom window (must be declared before any
  // early return to keep hook-order stable)
  const zoomedPeaks = useMemo(() => {
    if (!waveformPeaks || waveformPeaks.length === 0) return [];
    if (zoom.start <= 0.001 && zoom.end >= 0.999) return waveformPeaks;
    const startIdx = Math.floor(zoom.start * waveformPeaks.length);
    const endIdx = Math.max(startIdx + 1, Math.ceil(zoom.end * waveformPeaks.length));
    return waveformPeaks.slice(startIdx, Math.min(endIdx, waveformPeaks.length));
  }, [waveformPeaks, zoom]);

  const silenceRanges = asset?.silence_ranges || [];

  // Snap a time to the nearest detected silence boundary OR scene cut within
  // `tol` seconds. Enabled by the "Snap" toggle.
  const snapToSilence = (sec) => {
    if (!snapSilence || !Number.isFinite(sec)) return sec;
    const tol = snapTol;
    let best = sec, bestD = tol;
    for (const r of silenceRanges) {
      for (const b of [r.start, r.end]) {
        const d = Math.abs(b - sec);
        if (d < bestD) { bestD = d; best = b; }
      }
    }
    for (const c of sceneCuts) {
      const d = Math.abs(c - sec);
      if (d < bestD) { bestD = d; best = c; }
    }
    return best;
  };

  const detectScenes = async () => {
    setSceneBusy(true);
    try {
      const r = await api.post(`/assets/${asset.id}/detect-scenes?threshold=${sceneThreshold}`);
      setSceneCuts(r.data.scene_cuts || []);
      onUpdated?.({ ...asset, scene_cuts: r.data.scene_cuts });
      toast.success(`${r.data.count} scene cut(s) detected · snap to enable`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scene detection failed");
    }
    setSceneBusy(false);
  };

  // Zoom-adaptive refetch: when zoomed in, pull high-resolution peaks for just
  // the visible window so the waveform stays sharp for precise IN/OUT placement.
  useEffect(() => {
    if (!open || !asset?.id || rawDuration <= 0) return;
    const span = zoom.end - zoom.start;
    if (span >= 0.6) { setWindowPeaks(null); return; } // zoomed out → global peaks
    if (winTimerRef.current) clearTimeout(winTimerRef.current);
    winTimerRef.current = setTimeout(async () => {
      const startSec = zoom.start * rawDuration;
      const durSec = span * rawDuration;
      const reqId = ++winReqRef.current;
      setWinBusy(true);
      try {
        const r = await api.post(
          `/assets/${asset.id}/waveform?buckets=900&start_sec=${startSec.toFixed(3)}&dur_sec=${durSec.toFixed(3)}`);
        if (reqId === winReqRef.current && r.data?.windowed) {
          setWindowPeaks({ start: startSec, end: startSec + durSec, peaks: r.data.waveform_peaks || [] });
        }
      } catch { /* fall back to sliced global peaks */ }
      finally { if (reqId === winReqRef.current) setWinBusy(false); }
    }, 320);
    return () => winTimerRef.current && clearTimeout(winTimerRef.current);
  }, [open, asset?.id, zoom, rawDuration]);

  // Peaks actually drawn: prefer the high-res window when it matches the zoom.
  const displayPeaks = useMemo(() => {
    if (windowPeaks && windowPeaks.peaks.length) {
      const zs = zoom.start * rawDuration, ze = zoom.end * rawDuration;
      if (Math.abs(zs - windowPeaks.start) < 0.5 && Math.abs(ze - windowPeaks.end) < 0.5) {
        return windowPeaks.peaks;
      }
    }
    return zoomedPeaks;
  }, [windowPeaks, zoomedPeaks, zoom, rawDuration]);

  // Nudge IN/OUT by a signed delta (seconds), clamp, snap, and seek the playhead.
  const nudge = (which, delta) => {
    if (rawDuration <= 0) return;
    setResetIntent(false);
    if (which === "in") {
      let t = (tIn ?? 0) + delta;
      t = Math.max(0, Math.min(t, (tOut ?? rawDuration) - frameSec));
      t = snapToSilence(t);
      setTIn(t); setTInStr(secToStr(t));
      if (videoRef.current) videoRef.current.currentTime = t;
    } else {
      let t = (tOut ?? rawDuration) + delta;
      t = Math.min(rawDuration, Math.max(t, (tIn ?? 0) + frameSec));
      t = snapToSilence(t);
      setTOut(t); setTOutStr(secToStr(t));
      if (videoRef.current) videoRef.current.currentTime = t;
    }
  };

  // Keyboard shortcuts while the editor is open (ignored when typing in inputs).
  useEffect(() => {
    if (!open || !canEdit) return;
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      const v = videoRef.current;
      const k = e.key.toLowerCase();
      if (k === "i") { e.preventDefault(); setInHere(); }
      else if (k === "o") { e.preventDefault(); setOutHere(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); if (v) v.currentTime = Math.max(0, v.currentTime - (e.shiftKey ? 1 : frameSec)); }
      else if (e.key === "ArrowRight") { e.preventDefault(); if (v) v.currentTime = Math.min(rawDuration, v.currentTime + (e.shiftKey ? 1 : frameSec)); }
      else if (k === " ") { e.preventDefault(); if (v) { v.paused ? v.play().catch(() => {}) : v.pause(); } }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, canEdit, tIn, tOut, rawDuration, frameSec, snapSilence, silenceRanges]);

  // Native non-passive wheel listener so preventDefault works (React's onWheel
  // is passive and logs a console error when calling preventDefault).
  useEffect(() => {
    const el = scrubberRef.current;
    if (!el || !open) return;
    const handler = (e) => {
      if (!waveformPeaks.length || rawDuration <= 0) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width;
      const zs = zoom.start * rawDuration;
      const zspan = Math.max(0.001, (zoom.end - zoom.start) * rawDuration);
      const cursorNorm = (zs + px * zspan) / rawDuration;
      setZoom(({ start, end }) => {
        const span = end - start;
        const factor = e.deltaY > 0 ? 1.35 : 0.7;
        const newSpan = Math.max(0.01, Math.min(1, span * factor));
        const newStart = Math.max(0, Math.min(1 - newSpan, cursorNorm - (cursorNorm - start) * (newSpan / span)));
        return { start: newStart, end: newStart + newSpan };
      });
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [open, zoom, rawDuration, waveformPeaks.length]);

  if (!asset) return null;

  // % positions on the scrubber for the IN / OUT / playhead markers.
  // Zoom-aware: `zoom.start`/`zoom.end` are normalised (0..1) fractions of the
  // full asset duration. When zoomed in, we translate seconds → % of the
  // visible window (values outside the window are clipped to 0/100 and NOT
  // shown via `visible*` gates).
  const zoomStartSec = zoom.start * rawDuration;
  const zoomEndSec = zoom.end * rawDuration;
  const zoomSpan = Math.max(0.001, zoomEndSec - zoomStartSec);
  const zoomActive = zoom.start > 0.001 || zoom.end < 0.999;
  const pct = (t) => {
    if (rawDuration <= 0) return 0;
    return Math.min(100, Math.max(0, ((t - zoomStartSec) / zoomSpan) * 100));
  };
  const inVisible = tIn != null && tIn >= zoomStartSec && tIn <= zoomEndSec;
  const outVisible = tOut != null && tOut >= zoomStartSec && tOut <= zoomEndSec;
  const playVisible = currentTime >= zoomStartSec && currentTime <= zoomEndSec;
  const inPct = inVisible ? pct(tIn) : null;
  const outPct = outVisible ? pct(tOut) : null;
  const playPct = playVisible ? pct(currentTime) : null;

  const zoomIn = () => setZoom(({ start, end }) => {
    const span = end - start;
    if (span < 0.01) return { start, end }; // don't zoom past ~1% of the clip
    const mid = (start + end) / 2;
    const half = span / 2 * 0.6; // shrink to 60% of current span
    return { start: Math.max(0, mid - half), end: Math.min(1, mid + half) };
  });
  const zoomOut = () => setZoom(({ start, end }) => {
    const span = end - start;
    const mid = (start + end) / 2;
    const half = Math.min(0.5, span / 2 * 1.6);
    return { start: Math.max(0, mid - half), end: Math.min(1, mid + half) };
  });
  const zoomReset = () => setZoom({ start: 0, end: 1 });
  const zoomToTrim = () => {
    if (rawDuration <= 0) return;
    const inS = (tIn ?? 0);
    const outS = (tOut ?? rawDuration);
    const pad = Math.max(0.5, (outS - inS) * 0.1); // 10% padding on either side
    const s = Math.max(0, inS - pad) / rawDuration;
    const e = Math.min(rawDuration, outS + pad) / rawDuration;
    setZoom({ start: s, end: e });
  };

  const onScrubberMouseDown = (e) => {
    if (waveformPeaks.length === 0) return;
    // Shift+drag = pan; plain drag = seek playhead
    const rect = scrubberRef.current?.getBoundingClientRect();
    if (!rect) return;
    if (e.shiftKey) {
      dragRef.current = { kind: "pan", startX: e.clientX, startZoom: { ...zoom } };
    } else {
      // Seek: convert to absolute seconds and set playhead
      const px = (e.clientX - rect.left) / rect.width;
      const sec = zoomStartSec + px * zoomSpan;
      if (videoRef.current) videoRef.current.currentTime = sec;
    }
  };
  const onScrubberMouseMove = (e) => {
    if (!dragRef.current) return;
    const rect = scrubberRef.current?.getBoundingClientRect();
    if (!rect) return;
    if (dragRef.current.kind === "pan") {
      const dxPx = e.clientX - dragRef.current.startX;
      const dxFrac = (dxPx / rect.width) * (dragRef.current.startZoom.end - dragRef.current.startZoom.start);
      const s0 = dragRef.current.startZoom.start;
      const e0 = dragRef.current.startZoom.end;
      const span = e0 - s0;
      let ns = s0 - dxFrac;
      let ne = e0 - dxFrac;
      if (ns < 0) { ns = 0; ne = span; }
      if (ne > 1) { ne = 1; ns = 1 - span; }
      setZoom({ start: ns, end: ne });
    } else if (dragRef.current.kind === "in" || dragRef.current.kind === "out") {
      const px = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
      let sec = zoomStartSec + px * zoomSpan;
      if (dragRef.current.kind === "in") {
        sec = Math.max(0, Math.min(sec, (tOut ?? rawDuration) - frameSec));
        setTIn(sec); setTInStr(secToStr(sec));
      } else {
        sec = Math.min(rawDuration, Math.max(sec, (tIn ?? 0) + frameSec));
        setTOut(sec); setTOutStr(secToStr(sec));
      }
      // Ghost line: where this handle will land once snapped (shown pre-release).
      const snapped = snapToSilence(sec);
      setDragGhost(snapSilence && Math.abs(snapped - sec) > 0.001 ? snapped : null);
      if (videoRef.current) videoRef.current.currentTime = sec;
    }
  };
  const onScrubberMouseUp = () => {
    // Commit the handle to the snapped target if a ghost was showing.
    if (dragGhost != null && (dragRef.current?.kind === "in" || dragRef.current?.kind === "out")) {
      const g = Math.max(0, Math.min(dragGhost, rawDuration));
      if (dragRef.current.kind === "in") { setTIn(g); setTInStr(secToStr(g)); }
      else { setTOut(g); setTOutStr(secToStr(g)); }
    }
    setDragGhost(null);
    dragRef.current = null;
  };
  const startHandleDrag = (kind) => (e) => {
    e.stopPropagation();
    dragRef.current = { kind };
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-card border-border rounded-sm max-w-3xl p-0 max-h-[90vh] overflow-y-auto"
        data-testid="asset-preview-dialog"
      >
        <DialogHeader className="relative px-5 pt-5 pb-3">
          <div className="absolute inset-x-0 top-0 h-1" style={{ background: "var(--brand-gradient)" }} />
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-fuchsia-400" strokeWidth={1.75} />
            {asset.name}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground font-mono flex items-center gap-2 flex-wrap">
            <span>{asset.type?.toUpperCase()}</span>
            <span className="text-border">·</span>
            <span>{asset.resolution}</span>
            <span className="text-border">·</span>
            <span>{asset.codec}</span>
            <span className="text-border">·</span>
            <span data-testid="preview-effective-duration">
              {(tIn != null || tOut != null)
                ? <><span className="text-fuchsia-400">{fmtDur(effectiveDuration)}</span> <span className="text-muted-foreground/60">of {fmtDur(rawDuration)}</span></>
                : <>{fmtDur(rawDuration)}</>}
            </span>
            <span className="text-border">·</span>
            {usingCdn ? (
              <span className="text-emerald-500 flex items-center gap-1" data-testid="preview-source-cdn">
                <Cloud className="w-3 h-3" /> Cloudflare Stream HLS
              </span>
            ) : (
              <span className="text-muted-foreground flex items-center gap-1" data-testid="preview-source-origin">
                <HardDrive className="w-3 h-3" /> Origin MP4
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="relative bg-black aspect-video">
          {state.loading && !state.error && (
            <div className="absolute inset-0 flex items-center justify-center text-white">
              <Loader2 className="w-8 h-8 animate-spin" strokeWidth={1.5} />
            </div>
          )}
          {state.error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-rose-300 gap-2 p-6 text-center" data-testid="preview-error">
              <AlertCircle className="w-8 h-8" strokeWidth={1.5} />
              <div className="font-display font-bold">{state.error}</div>
              <div className="text-xs text-white/60 font-mono max-w-md">
                {asset.codec === "H265" || asset.codec === "HEVC"
                  ? "HEVC/H.265 support varies by browser. Try Safari or a modern Chromium."
                  : "Try downloading the file to check locally, or re-uploading in a different container."}
              </div>
            </div>
          )}
          <video
            ref={attachVideo}
            controls
            playsInline
            crossOrigin="anonymous"
            preload="metadata"
            className="w-full h-full"
            data-testid="asset-preview-video"
          />
          {loopFlash && (
            <div className="absolute inset-0 bg-black pointer-events-none transition-opacity" data-testid="loop-flash" />
          )}
          {loopPreview && (
            <div className="absolute top-2 left-2 px-2 py-1 rounded-sm bg-black/70 border border-emerald-400/40 text-emerald-300 font-mono text-[11px] pointer-events-none" data-testid="loop-duration-hud">
              IN→OUT = {secToStr(Math.max(0, (tOut ?? rawDuration) - (tIn ?? 0)))}
            </div>
          )}
        </div>

        {/* Trim editor */}
        <div className="px-5 py-4 border-t border-border space-y-3" data-testid="trim-editor">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-display font-bold text-sm">
              <Scissors className="w-4 h-4 text-fuchsia-400" strokeWidth={1.75} />
              Trim in / out
              {(tIn != null || tOut != null) && (
                <span className="text-[10px] font-mono uppercase tracking-widest text-fuchsia-400 border border-fuchsia-500/40 rounded-sm px-1.5 py-0.5"
                      data-testid="trim-active-pill">
                  ACTIVE
                </span>
              )}
            </div>
            {!canEdit && (
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-testid="trim-readonly">
                READ ONLY · your role can&apos;t edit
              </span>
            )}
          </div>

          {/* Custom scrubber with waveform + IN / OUT / playhead markers */}
          <div
            ref={scrubberRef}
            className="relative h-16 rounded-sm bg-muted/40 overflow-hidden select-none"
            style={{ cursor: dragRef.current?.kind === "pan" ? "grabbing" : (waveformPeaks.length ? "crosshair" : "default") }}
            onMouseDown={onScrubberMouseDown}
            onMouseMove={onScrubberMouseMove}
            onMouseUp={onScrubberMouseUp}
            onMouseLeave={onScrubberMouseUp}
            data-testid="trim-scrubber"
          >
            {/* Waveform SVG (behind everything) */}
            {displayPeaks.length > 0 && (
              <svg
                viewBox={`0 0 ${displayPeaks.length} 100`}
                preserveAspectRatio="none"
                className="absolute inset-0 w-full h-full"
                data-testid="waveform-svg"
              >
                {displayPeaks.map((p, i) => {
                  const h = Math.max(2, p * 90);
                  const y = (100 - h) / 2;
                  return (
                    <rect
                      key={i}
                      x={i}
                      y={y}
                      width="0.9"
                      height={h}
                      fill="currentColor"
                      className={windowPeaks && displayPeaks === windowPeaks.peaks ? "text-cyan-400/50" : "text-fuchsia-500/40"}
                    />
                  );
                })}
              </svg>
            )}
            {waveformPeaks.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center text-[10px] font-mono uppercase tracking-widest text-muted-foreground/60">
                {waveformBusy ? "generating waveform…" : "no waveform · click ‘Generate waveform’ below"}
              </div>
            )}
            {/* Detected silence bands (dead air) within the visible window */}
            {silenceRanges.map((r, i) => {
              const vs = Math.max(r.start, zoomStartSec);
              const ve = Math.min(r.end, zoomEndSec);
              if (ve <= vs) return null;
              return (
                <div
                  key={`sil-${i}`}
                  className="absolute top-0 bottom-0 bg-amber-400/10 border-x border-amber-400/25 pointer-events-none"
                  style={{ left: `${pct(vs)}%`, width: `${pct(ve) - pct(vs)}%` }}
                  title="Detected silence"
                />
              );
            })}
            {winBusy && (
              <div className="absolute top-0.5 right-1 text-[8px] font-mono text-cyan-300/80 pointer-events-none" data-testid="waveform-hires-busy">sharpening…</div>
            )}
            {/* Snap ghost line — where a dragged IN/OUT handle will land */}
            {dragGhost != null && dragGhost >= zoomStartSec && dragGhost <= zoomEndSec && (
              <div
                className="absolute top-0 bottom-0 w-[2px] bg-cyan-300 pointer-events-none"
                style={{ left: `${pct(dragGhost)}%`, boxShadow: "0 0 6px rgba(103,232,249,0.9)" }}
                data-testid="snap-ghost-line"
              >
                <div className="absolute -top-1 -translate-x-1/2 text-[8px] font-mono text-cyan-300 whitespace-nowrap">snap {secToStr(dragGhost)}</div>
              </div>
            )}
            {/* Scene-cut markers within the visible window */}
            {sceneCuts.map((c, i) => {
              if (c < zoomStartSec || c > zoomEndSec) return null;
              return (
                <div
                  key={`cut-${i}`}
                  className="absolute top-0 bottom-0 w-px bg-sky-400/50 pointer-events-none"
                  style={{ left: `${pct(c)}%` }}
                  title={`Scene cut: ${secToStr(c)}`}
                  data-testid={`scene-cut-${i}`}
                />
              );
            })}
            {windowPeaks && displayPeaks === windowPeaks.peaks && (
              <div className="absolute top-0.5 left-1/2 -translate-x-1/2 text-[8px] font-mono text-cyan-300/80 pointer-events-none" data-testid="waveform-hires-badge">hi-res</div>
            )}
            {/* Trimmed region highlight */}
            {rawDuration > 0 && (tIn != null || tOut != null) && (
              (() => {
                // Compute visible portion of the trim region in the zoomed window
                const inSec = tIn ?? 0;
                const outSec = tOut ?? rawDuration;
                const visibleStart = Math.max(inSec, zoomStartSec);
                const visibleEnd = Math.min(outSec, zoomEndSec);
                if (visibleEnd <= visibleStart) return null;
                return (
                  <div
                    className="absolute top-0 bottom-0 bg-fuchsia-500/15 border-x border-fuchsia-500/60 pointer-events-none"
                    style={{ left: `${pct(visibleStart)}%`, width: `${pct(visibleEnd) - pct(visibleStart)}%` }}
                  />
                );
              })()
            )}
            {/* IN marker */}
            {inPct != null && (
              <div
                className="absolute top-0 bottom-0 w-[2px] bg-emerald-400"
                style={{ left: `${inPct}%` }}
                title={`IN: ${secToStr(tIn)}`}
                data-testid="marker-in"
              >
                <div className="absolute -top-1 -translate-x-1/2 text-[8px] font-mono text-emerald-400 pointer-events-none">IN</div>
                {canEdit && (
                  <div
                    className="absolute top-2 -left-1.5 w-3 h-4 bg-emerald-400/80 rounded-sm cursor-ew-resize hover:bg-emerald-400"
                    onMouseDown={startHandleDrag("in")}
                    data-testid="handle-in"
                    title="Drag to move IN"
                  />
                )}
              </div>
            )}
            {/* OUT marker */}
            {outPct != null && (
              <div
                className="absolute top-0 bottom-0 w-[2px] bg-rose-400"
                style={{ left: `${outPct}%` }}
                title={`OUT: ${secToStr(tOut)}`}
                data-testid="marker-out"
              >
                <div className="absolute -top-1 -translate-x-1/2 text-[8px] font-mono text-rose-400 pointer-events-none">OUT</div>
                {canEdit && (
                  <div
                    className="absolute top-2 -right-1.5 w-3 h-4 bg-rose-400/80 rounded-sm cursor-ew-resize hover:bg-rose-400"
                    onMouseDown={startHandleDrag("out")}
                    data-testid="handle-out"
                    title="Drag to move OUT"
                  />
                )}
              </div>
            )}
            {/* Playhead */}
            {playPct != null && (
              <div
                className="absolute top-0 bottom-0 w-[1px] bg-white pointer-events-none"
                style={{ left: `${playPct}%` }}
                data-testid="marker-playhead"
              />
            )}
            {/* Time labels: left edge, right edge, playhead */}
            <div className="absolute top-0.5 left-1 text-[9px] font-mono text-muted-foreground bg-background/60 px-1 rounded pointer-events-none">
              {secToStr(zoomStartSec)}
            </div>
            <div className="absolute top-0.5 right-1 text-[9px] font-mono text-muted-foreground bg-background/60 px-1 rounded pointer-events-none">
              {secToStr(zoomEndSec)}
            </div>
            <div className="absolute bottom-0.5 right-1 text-[9px] font-mono text-muted-foreground bg-background/60 px-1 rounded pointer-events-none">
              {secToStr(currentTime)} / {secToStr(rawDuration)}
            </div>
            {zoomActive && (
              <div className="absolute bottom-0.5 left-1 text-[9px] font-mono text-fuchsia-400 bg-background/60 px-1 rounded pointer-events-none" data-testid="zoom-pill">
                zoom · {(1 / (zoom.end - zoom.start)).toFixed(1)}×
              </div>
            )}
          </div>

          {/* Zoom mini-map (only when zoomed in) */}
          {zoomActive && waveformPeaks.length > 0 && (
            <div className="relative h-3 rounded-sm bg-muted/20 overflow-hidden" data-testid="zoom-minimap">
              <div
                className="absolute top-0 bottom-0 bg-fuchsia-500/25 border-x border-fuchsia-500/70"
                style={{ left: `${zoom.start * 100}%`, width: `${(zoom.end - zoom.start) * 100}%` }}
              />
            </div>
          )}

          {/* Zoom controls (available to everyone, not just editors) */}
          {waveformPeaks.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              <span>zoom:</span>
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm h-6 px-1.5 text-[10px] border-border"
                onClick={zoomIn}
                disabled={zoom.end - zoom.start < 0.02}
                data-testid="zoom-in-btn"
                title="Zoom in (or scroll ↑ on the waveform)"
              >
                <ZoomIn className="w-3 h-3" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm h-6 px-1.5 text-[10px] border-border"
                onClick={zoomOut}
                disabled={!zoomActive}
                data-testid="zoom-out-btn"
                title="Zoom out (or scroll ↓ on the waveform)"
              >
                <ZoomOut className="w-3 h-3" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm h-6 px-1.5 text-[10px] border-border"
                onClick={zoomReset}
                disabled={!zoomActive}
                data-testid="zoom-reset-btn"
                title="Zoom to full clip"
              >
                <Maximize2 className="w-3 h-3" />
              </Button>
              {(tIn != null || tOut != null) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm h-6 px-2 text-[10px] border-fuchsia-500/40 text-fuchsia-500"
                  onClick={zoomToTrim}
                  data-testid="zoom-to-trim-btn"
                  title="Fit view to trimmed region + 10% padding"
                >
                  fit trim
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className={`rounded-sm h-6 px-2 text-[10px] ${loopPreview ? "border-emerald-400/60 text-emerald-400 bg-emerald-400/10" : "border-border"}`}
                onClick={toggleLoopPreview}
                data-testid="loop-preview-btn"
                title="Play only IN→OUT on a loop to eyeball the edit"
              >
                {loopPreview ? <Pause className="w-3 h-3 mr-1" /> : <Repeat className="w-3 h-3 mr-1" />}
                Loop IN→OUT
              </Button>
              <span className="opacity-60">· scroll to zoom · shift+drag pan · I/O set · ←/→ frame (shift ±1s)</span>
            </div>
          )}

          {/* Silence-detect tunables + quick actions */}
          {canEdit && (
            <div className="space-y-2 border-t border-border pt-2">
              <div className="flex items-center gap-1.5 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm h-7 text-xs border-border"
                  onClick={detectSilence}
                  disabled={silenceBusy}
                  data-testid="trim-detect-silence"
                  title="Auto-suggest IN/OUT from leading & trailing silence"
                >
                  {silenceBusy
                    ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    : <VolumeX className="w-3 h-3 mr-1" />}
                  Detect silence
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={`rounded-sm h-7 text-xs border-border ${snapSilence ? "border-amber-400/60 text-amber-400 bg-amber-400/10" : ""}`}
                  onClick={() => setSnapSilence((s) => !s)}
                  data-testid="trim-snap-silence-toggle"
                  title="When on, IN/OUT snap to the nearest detected silence boundary"
                >
                  <VolumeX className="w-3 h-3 mr-1" />
                  Snap {snapSilence ? "ON" : "OFF"}{sceneCuts.length ? " (sil+cuts)" : " (silence)"}
                </Button>
                {snapSilence && (
                  <select
                    value={snapTol}
                    onChange={(e) => setSnapTol(parseFloat(e.target.value))}
                    className="rounded-sm h-7 text-[10px] font-mono bg-background border border-border px-1"
                    data-testid="trim-snap-tolerance"
                    title="Snap strength — how close IN/OUT must be to jump to a cut/silence"
                  >
                    <option value={0.25}>tight (0.25s)</option>
                    <option value={0.5}>0.5s</option>
                    <option value={0.75}>default (0.75s)</option>
                    <option value={1.5}>loose (1.5s)</option>
                    <option value={3}>very loose (3s)</option>
                  </select>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm h-7 text-xs border-border"
                  onClick={detectScenes}
                  disabled={sceneBusy}
                  data-testid="trim-detect-scenes"
                  title="Detect scene cuts so IN/OUT can snap to visual cuts"
                >
                  {sceneBusy
                    ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    : <Scissors className="w-3 h-3 mr-1" />}
                  Detect cuts{sceneCuts.length ? ` (${sceneCuts.length})` : ""}
                </Button>
                <select
                  value={sceneThreshold}
                  onChange={(e) => setSceneThreshold(parseFloat(e.target.value))}
                  className="rounded-sm h-7 text-[10px] font-mono bg-background border border-border px-1"
                  data-testid="trim-scene-sensitivity"
                  title="Scene-cut sensitivity — lower catches softer transitions"
                >
                  <option value={0.2}>soft (0.2)</option>
                  <option value={0.3}>med-soft (0.3)</option>
                  <option value={0.4}>default (0.4)</option>
                  <option value={0.5}>strict (0.5)</option>
                  <option value={0.6}>hard cuts (0.6)</option>
                </select>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm h-7 text-xs border-border"
                  onClick={regenerateWaveform}
                  disabled={waveformBusy}
                  data-testid="trim-gen-waveform"
                  title="Generate a high-resolution (1200-bucket) audio waveform"
                >
                  {waveformBusy
                    ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    : <Wand2 className="w-3 h-3 mr-1" />}
                  {waveformPeaks.length >= 1000 ? "Regenerate hi-res" : waveformPeaks.length ? "Upgrade to hi-res" : "Generate waveform"}
                </Button>
                {(asset?.suggested_trim_in_sec != null || asset?.suggested_trim_out_sec != null) && (
                  <span className="text-[10px] font-mono text-muted-foreground" data-testid="silence-hint">
                    prior: {asset.suggested_trim_in_sec != null ? `IN ${secToStr(asset.suggested_trim_in_sec)}` : "—"}
                    {" · "}
                    {asset.suggested_trim_out_sec != null ? `OUT ${secToStr(asset.suggested_trim_out_sec)}` : "—"}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 text-[10px] font-mono">
                <div>
                  <div className="flex justify-between items-center">
                    <Label className="uppercase tracking-widest text-muted-foreground">noise floor</Label>
                    <span className="text-foreground">{silenceThresholdDb} dB</span>
                  </div>
                  <Slider
                    className="mt-1"
                    value={[silenceThresholdDb]}
                    onValueChange={(v) => setSilenceThresholdDb(v[0])}
                    min={-60}
                    max={-10}
                    step={1}
                    data-testid="silence-threshold-slider"
                  />
                  <div className="flex justify-between text-muted-foreground/60 mt-0.5">
                    <span>-60 quiet</span>
                    <span>-30 default</span>
                    <span>-10 loud</span>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center">
                    <Label className="uppercase tracking-widest text-muted-foreground">min duration</Label>
                    <span className="text-foreground">{silenceMinDur.toFixed(2)} s</span>
                  </div>
                  <Slider
                    className="mt-1"
                    value={[silenceMinDur]}
                    onValueChange={(v) => setSilenceMinDur(v[0])}
                    min={0.1}
                    max={3.0}
                    step={0.1}
                    data-testid="silence-min-dur-slider"
                  />
                  <div className="flex justify-between text-muted-foreground/60 mt-0.5">
                    <span>0.1s music</span>
                    <span>0.4s default</span>
                    <span>3.0s drama</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Set-here buttons + numeric fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                IN point
              </Label>
              <div className="flex items-stretch gap-1">
                <Input
                  className="rounded-sm font-mono text-xs h-8"
                  placeholder="00:00:00.000"
                  value={tInStr}
                  onChange={(e) => setTInStr(e.target.value)}
                  onBlur={applyInStr}
                  disabled={!canEdit}
                  data-testid="trim-in-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm h-8 shrink-0"
                  onClick={setInHere}
                  disabled={!canEdit || !rawDuration}
                  title="Set IN to current playhead"
                  data-testid="trim-set-in"
                >
                  <LogIn className="w-3.5 h-3.5 mr-1" /> Set IN
                </Button>
                {tIn != null && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-sm h-8 shrink-0"
                    onClick={jumpToIn}
                    title="Seek playhead to IN"
                    data-testid="trim-jump-in"
                  >→</Button>
                )}
              </div>
              {canEdit && (
                <div className="flex items-center gap-1 pt-0.5" data-testid="trim-in-nudge">
                  <span className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground/60 mr-1">nudge</span>
                  <Button variant="outline" size="sm" className="rounded-sm h-6 px-1.5 text-[10px]" onClick={() => nudge("in", -1)} data-testid="trim-in-minus-1s" title="IN −1s">−1s</Button>
                  <Button variant="outline" size="sm" className="rounded-sm h-6 px-1.5 text-[10px]" onClick={() => nudge("in", -frameSec)} data-testid="trim-in-minus-frame" title="IN −1 frame">−1f</Button>
                  <Button variant="outline" size="sm" className="rounded-sm h-6 px-1.5 text-[10px]" onClick={() => nudge("in", frameSec)} data-testid="trim-in-plus-frame" title="IN +1 frame">+1f</Button>
                  <Button variant="outline" size="sm" className="rounded-sm h-6 px-1.5 text-[10px]" onClick={() => nudge("in", 1)} data-testid="trim-in-plus-1s" title="IN +1s">+1s</Button>
                </div>
              )}
            </div>

            <div className="space-y-1">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                OUT point
              </Label>
              <div className="flex items-stretch gap-1">
                <Input
                  className="rounded-sm font-mono text-xs h-8"
                  placeholder={secToStr(rawDuration)}
                  value={tOutStr}
                  onChange={(e) => setTOutStr(e.target.value)}
                  onBlur={applyOutStr}
                  disabled={!canEdit}
                  data-testid="trim-out-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm h-8 shrink-0"
                  onClick={setOutHere}
                  disabled={!canEdit || !rawDuration}
                  title="Set OUT to current playhead"
                  data-testid="trim-set-out"
                >
                  <LogOut className="w-3.5 h-3.5 mr-1" /> Set OUT
                </Button>
                {tOut != null && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-sm h-8 shrink-0"
                    onClick={jumpToOut}
                    title="Seek playhead to OUT"
                    data-testid="trim-jump-out"
                  >→</Button>
                )}
              </div>
              {canEdit && (
                <div className="flex items-center gap-1 pt-0.5" data-testid="trim-out-nudge">
                  <span className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground/60 mr-1">nudge</span>
                  <Button variant="outline" size="sm" className="rounded-sm h-6 px-1.5 text-[10px]" onClick={() => nudge("out", -1)} data-testid="trim-out-minus-1s" title="OUT −1s">−1s</Button>
                  <Button variant="outline" size="sm" className="rounded-sm h-6 px-1.5 text-[10px]" onClick={() => nudge("out", -frameSec)} data-testid="trim-out-minus-frame" title="OUT −1 frame">−1f</Button>
                  <Button variant="outline" size="sm" className="rounded-sm h-6 px-1.5 text-[10px]" onClick={() => nudge("out", frameSec)} data-testid="trim-out-plus-frame" title="OUT +1 frame">+1f</Button>
                  <Button variant="outline" size="sm" className="rounded-sm h-6 px-1.5 text-[10px]" onClick={() => nudge("out", 1)} data-testid="trim-out-plus-1s" title="OUT +1s">+1s</Button>
                </div>
              )}
            </div>
          </div>

          {/* Save / Revert / Clear */}
          {canEdit && (
            <div className="flex items-center justify-between pt-1">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                Effective on air: <span className="text-fuchsia-400">{fmtDur(effectiveDuration)}</span>
                {(tIn != null || tOut != null) && (
                  <> · saved everywhere Scheduler / Grid / EPG · undoable anytime</>
                )}
              </div>
              <div className="flex items-center gap-1">
                {((tIn != null && tIn > 0.001) || (tOut != null && tOut < rawDuration - 0.05)) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-sm h-8 border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/10"
                    onClick={resetToFull}
                    data-testid="trim-reset-full"
                    title="Reset IN to 0 and OUT to the full clip length"
                  >
                    <Maximize2 className="w-3.5 h-3.5 mr-1" /> Reset to full length
                  </Button>
                )}
                {(tIn != null || tOut != null) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-sm h-8 border-rose-500/40 text-rose-500 hover:bg-rose-500/10"
                    onClick={clearTrim}
                    data-testid="trim-clear"
                    title="Clear both endpoints"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Clear
                  </Button>
                )}
                {dirty && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-sm h-8"
                    onClick={revert}
                    data-testid="trim-revert"
                    title="Revert to saved values"
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1" /> Revert
                  </Button>
                )}
                <Button
                  size="sm"
                  className="rounded-sm h-8 tenant-btn"
                  onClick={save}
                  disabled={saving || !dirty}
                  data-testid="trim-save"
                >
                  <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Saving…" : "Save trim"}
                </Button>
              </div>
            </div>
          )}
          {confirmEndPlay && (
              <div className="rounded-sm border border-amber-500/50 bg-amber-500/10 px-3 py-2.5 flex items-center justify-between gap-3" data-testid="trim-endplay-confirm">
                <span className="text-xs text-amber-200">This song will play to the end. Continue?</span>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="outline" size="sm" className="rounded-sm h-7" onClick={() => setConfirmEndPlay(false)} data-testid="trim-endplay-cancel">Cancel</Button>
                  <Button size="sm" className="rounded-sm h-7 tenant-btn" onClick={() => { setConfirmEndPlay(false); doSave(resetIntent); }} data-testid="trim-endplay-continue">Continue</Button>
                </div>
              </div>
            )}
            {!overrideMode && Array.isArray(asset?.trim_history) && asset.trim_history.length > 0 && (
              <div className="pt-1" data-testid="trim-history">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">IN / OUT history</div>
                <div className="space-y-1 max-h-32 overflow-auto pr-1">
                  {[...asset.trim_history].reverse().map((h, i) => (
                    <div key={i} className="text-[11px] text-muted-foreground flex items-center gap-2" data-testid="trim-history-row">
                      <span className="text-foreground/80 whitespace-nowrap">{fmtWhen(h.at)}</span>
                      <span className="truncate max-w-[150px]" title={h.by_email || h.by}>{h.by_name || h.by_email || h.by || "unknown"}</span>
                      <span className="font-mono whitespace-nowrap">
                        {h.reset_full
                          ? "→ full length"
                          : `${trimLabel(h.old_in_sec, h.old_out_sec)} → ${trimLabel(h.new_in_sec, h.new_out_sec)}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

        <div className="px-5 py-3 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-t border-border">
          <span>{usingCdn ? "HLS via edge · adaptive bitrate" : "Progressive MP4 · byte-range seek enabled"}</span>
          <span className="truncate max-w-[280px]">{usingCdn ? asset.cloudflare_stream_uid : `/api/assets/file/${asset.id}`}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
